import React, { useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox,
} from '@mui/material';
import ExportMenu, { FORMATS } from './ExportMenu';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { exportService } from '../../services/accounting/export.service';

const ACCOUNTANT = { quickbooks: 'QuickBooks', zoho: 'Zoho Books', tally: 'Tally' };

/**
 * Export of the journal entries (BE-14b) for a period: PDF and CSV straight away; QuickBooks, Zoho Books and Tally
 * through a dialog with the one-time setup notes (and, for Tally, the ledgers for a new company).
 */
const JournalExportMenu = ({ from, to, onError, filtered }) => {
  const [busy, setBusy] = useState(false);
  const [accountant, setAccountant] = useState(null);
  const [masters, setMasters] = useState(false);

  const download = async (format, extra = {}) => {
    setBusy(true);
    try {
      await exportService.download('journal-entries', format, { from, to, ...extra });
      return true;
    } catch (err) {
      if (onError) onError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleExport = (format) => {
    if (ACCOUNTANT[format]) {
      setMasters(false);
      setAccountant(format);
    } else {
      download(format);
    }
  };

  const confirm = async () => {
    const done = await download(accountant, accountant === 'tally' && masters ? { masters: true } : {});
    if (done) setAccountant(null);
  };

  return (
    <>
      <ExportMenu onExport={handleExport} busy={busy} formats={FORMATS} />
      <Dialog open={Boolean(accountant)} onClose={() => setAccountant(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Export to {ACCOUNTANT[accountant]}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: fs.sm, mb: 1 }}>
            Journal entries from {from} to {to}{filtered ? ' — every entry in the period; the type and account filters do not apply' : ''}.
          </Typography>
          <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 1.5 }}>
            <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, mb: 0.5 }}>Before the first import</Typography>
            <Typography component="ul" sx={{ fontSize: fs.xs, m: 0, pl: 2 }}>
              <li>Import the chart of accounts{accountant === 'tally' ? ' (the Tally ledgers)' : ''} first, so every account exists.</li>
              {accountant === 'quickbooks' && (
                <li>In QuickBooks create the customer "Walk-in customer" and the supplier "Sundry supplier" — lines without a customer or supplier (such as the opening balance) use them.</li>
              )}
              <li>The VAT return is filed from Thaklis, not from {ACCOUNTANT[accountant]}.</li>
            </Typography>
          </Box>
          {accountant === 'tally' && (
            <FormControlLabel
              control={<Checkbox size="small" checked={masters} onChange={(e) => setMasters(e.target.checked)} />}
              label={<Typography sx={{ fontSize: fs.sm }}>Include ledgers (for a new Tally company) — downloads a ZIP</Typography>}
            />
          )}
          {accountant === 'quickbooks' && (
            <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
              Over 998 lines the file comes as a ZIP of parts, each imported in turn.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAccountant(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={confirm} disabled={busy} sx={{ textTransform: 'none' }}>
            {busy ? 'Exporting…' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JournalExportMenu;
